import { FC, useState, useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import type { ScoringType } from "@/lib/scoring/types";
import {
  CRITERION_REGISTRY,
  CRITERION_LABELS,
  getCriterionKey,
} from "@/lib/scoring/criterionRegistry";

interface CriterionEditorDialogProps {
  open: boolean;
  criterion: ScoringType | null;
  isNew: boolean;
  onConfirm: (updated: ScoringType) => void;
  onCancel: () => void;
}

export const CriterionEditorDialog: FC<CriterionEditorDialogProps> = ({
  open,
  criterion,
  isNew,
  onConfirm,
  onCancel,
}) => {
  const [draft, setDraft] = useState<ScoringType | null>(null);

  useEffect(() => {
    if (open && criterion) setDraft(criterion);
  }, [open, criterion]);

  if (!draft) return null;

  const key = getCriterionKey(draft);
  const entry = key ? CRITERION_REGISTRY[key] : undefined;
  const Editor = entry?.editor;

  const hasBandErrors = entry?.hasErrors
    ? entry.hasErrors((draft[key!] as unknown[]) ?? [])
    : false;

  const isConfirmDisabled = !key || !Editor || hasBandErrors;

  const displayName = draft.name || (key ? CRITERION_LABELS[key] : "");
  const title = isNew
    ? "New Scoring Type"
    : `Edit ${CRITERION_LABELS[key!] ?? displayName}`;

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          {title}
          {entry?.tooltip && (
            <Tooltip title={entry.tooltip} placement="right">
              <IconButton
                size="small"
                tabIndex={-1}
                sx={{ color: "text.secondary" }}
              >
                <InfoOutlinedIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ minHeight: 420 }}>
        <Box sx={{ mb: 2 }}>
          <TextField
            label="Name"
            size="small"
            fullWidth
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            onBlur={() => {
              if (!draft.name.trim() && key) {
                setDraft({ ...draft, name: CRITERION_LABELS[key] });
              }
            }}
          />
        </Box>
        {Editor && (
          <Editor criterion={draft} onChange={(updated) => setDraft(updated)} />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} color="inherit">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => onConfirm(draft)}
          disabled={isConfirmDisabled}
        >
          {isNew ? "ADD" : "UPDATE"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
